import { useState } from 'react'
import Modal from '../components/Modal'
import { ErrorText } from '../components/Form'
import { ShowFields, initialShowDraft, showIsValid } from '../forms/ShowFields'
import { createShow, updateShow, deleteShow } from '../lib/mutations'
import type { ShowDraft } from '../lib/mutations'
import type { Show, WithId } from '../types/models'

export default function ShowDialog({
  open,
  show,
  tourId,
  userId,
  defaultDate,
  onClose,
}: {
  open: boolean
  show?: WithId<Show> | null
  tourId: string
  userId: string
  defaultDate?: string
  onClose: () => void
}) {
  const [draft, setDraft] = useState<ShowDraft>(() => initialShowDraft(show, defaultDate))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const patch = (next: Partial<ShowDraft>) => setDraft((previous) => ({ ...previous, ...next }))

  const valid = showIsValid(draft)

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      if (show) await updateShow(show, draft)
      else await createShow(draft, tourId, userId)
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!show) return
    setBusy(true)
    setError(null)
    try {
      await deleteShow(show)
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={show ? 'Edit show' : 'Add show'}
      width={580}
      onClose={onClose}
      footer={
        <>
          {show && (
            <button
              className="btn btn-danger"
              disabled={busy}
              style={{ marginRight: 'auto' }}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : show ? 'Save show' : 'Add show'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 16 }}>
        <ShowFields draft={draft} patch={patch} />

        <ErrorText>{error}</ErrorText>

        {confirmingDelete && show && (
          <div
            className="stack"
            style={{
              gap: 8,
              padding: 14,
              borderRadius: 'var(--radius-control)',
              background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
            }}
          >
            <strong>Delete {show.city}?</strong>
            <p className="t-caption">
              The show and its running-order items come off the itinerary too.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-danger" onClick={() => void remove()} disabled={busy}>
                {busy ? 'Deleting…' : 'Delete show'}
              </button>
              <button className="btn btn-quiet" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
